import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { client } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { toast } from 'sonner';

export default function SettingsPage() {
  const queryClient = useQueryClient();

  const { data: userSettings } = useQuery({
    queryKey: ['user-settings'],
    queryFn: async () => {
      const res = await client.api['user-settings'].me.$get();
      if (!res.ok) throw new Error('Failed to fetch user settings');
      return res.json();
    },
  });

  const [signature, setSignature] = useState('');
  const [signatureDirty, setSignatureDirty] = useState(false);

  useEffect(() => {
    if (userSettings && !signatureDirty) {
      setSignature(userSettings.emailSignature ?? '');
    }
  }, [userSettings, signatureDirty]);

  const saveSignatureMutation = useMutation({
    mutationFn: async (value: string) => {
      const res = await client.api['user-settings'].me.$put({
        json: { emailSignature: value.trim() === '' ? null : value },
      });
      if (!res.ok) throw new Error('Failed to save signature');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-settings'] });
      setSignatureDirty(false);
      toast.success('Signature saved');
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  return (
    <div className="space-y-10">
      <div className="space-y-1">
        <h1 className="display-tight text-3xl font-semibold">Settings</h1>
        <p className="text-sm text-muted-foreground">
          Manage your account preferences.
        </p>
      </div>

      <Card className="rounded-3xl bg-card shadow-ambient">
        <CardHeader className="pb-2">
          <p className="label-caps text-xs text-muted-foreground">Email Signature</p>
          <p className="text-sm text-muted-foreground">
            Used to pre-fill the closing of vendor inquiry emails. You can still edit per email before sending.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <Textarea
            value={signature}
            onChange={(e) => {
              setSignature(e.target.value);
              setSignatureDirty(true);
            }}
            rows={6}
            placeholder={'Thank you,\nJane Doe\nLead Planner, Everplan\n(555) 123-4567'}
            className="font-mono text-sm"
          />
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!signatureDirty || saveSignatureMutation.isPending}
              onClick={() => {
                setSignature(userSettings?.emailSignature ?? '');
                setSignatureDirty(false);
              }}
            >
              Reset
            </Button>
            <Button
              size="sm"
              disabled={!signatureDirty || saveSignatureMutation.isPending}
              onClick={() => saveSignatureMutation.mutate(signature)}
            >
              {saveSignatureMutation.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
